import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Col, FormGroup, Label, Row } from "reactstrap";
import { useHistory } from "react-router-dom";
import { MetaTags } from "react-meta-tags";
import { C_Button } from "../../components/Common/CommonButton";
import { C_DatePicker, C_Select } from "../../CustomValidateForm";
import * as _cfunc from "../../components/Common/CommonFunction";
import { mode } from "../../routes/index";
import { customAlert } from "../../CustomAlert/ConfirmDialog";
import { alertMessages } from "../../components/Common/CommonErrorMsg/alertMsg";
import { ExcelReportComponent } from "../../components/Common/ReportCommonFunc/ExcelDownloadWithCSS";
import * as report from '../ReportIndex'
import { CashBookReportAPI, CashierName_Api } from "../../helpers/backend_helper";
import { getpdfReportdata, getpdfReportdataSuccess } from "../../store/actions";
import C_Report from "../../components/Common/C_Report";
import { allLabelWithZero } from "../../components/Common/CommonErrorMsg/HarderCodeData";


// Excel rows: match the PDF closing row values
const buildExcelRows = (result) =>
    (result.CashBookDetails || []).map((r) => {
        const isClosing = r.Particulars === "Closing Balance";

        return {
            "Entry Date": r.EntryDate
                ? _cfunc.date_dmy_func(r.EntryDate)
                : "",
            "Particulars": r.Particulars,
            "In Amount": isClosing
                ? Number(result.TotalInAmount) || 0
                : Number(r.InAmount) || 0,
            "Out Amount": isClosing
                ? Number(result.TotalOutAmount) || 0
                : Number(r.OutAmount) || 0,
            "Balance": isClosing
                ? Number(result.ClosingBalance) || 0
                : Number(r.Balance) || 0,
        };
    });
const CashBookEntryReport = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const currentDate_ymd = _cfunc.date_ymd_func();

    const [fromDate, setFromDate] = useState(currentDate_ymd);
    const [toDate, setToDate] = useState(currentDate_ymd);
    const [userPageAccessState, setUserAccState] = useState("");
    const [cashier, setCashier] = useState(allLabelWithZero);
    const [cashierOptions, setCashierOptions] = useState([allLabelWithZero]);
    const [cashierLoading, setCashierLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [clickedBtn, setClickedBtn] = useState("");     // "print" | "excel" | ""

    const { userAccess, pdfdata, goBtnLoading } = useSelector((state) => ({
        userAccess: state.Login.RoleAccessUpdateData,
        pdfdata: state.PdfReportReducers.pdfdata,
        goBtnLoading: state.PdfReportReducers.goBtnLoading,
    }));
    // Party comes from the common Party dropdown in the page header
    const { commonPartyDropSelect } = useSelector((state) => state.CommonPartyDropdownReducer);

    const location = { ...history.location };
    const hasShowModal = props.hasOwnProperty(mode.editValue);

    useEffect(() => {
        const locationPath = hasShowModal ? props.masterPath : location.pathname;
        const userAcc = userAccess.find((inx) => `/${inx.ActualPagePath}` === locationPath);
        if (userAcc) {
            setUserAccState(userAcc);
            _cfunc.breadcrumbReturnFunc({ dispatch, userAcc });
        }
    }, [userAccess]);

    useEffect(() => {
        _cfunc.saveLogoToLocalStorage();     // PDF header uses the saved company logo
    }, []);

    useEffect(() => {
        if (pdfdata.Status === true && pdfdata.StatusCode === 204) {
            dispatch(getpdfReportdataSuccess({ Status: false }));
            customAlert({ Type: 3, Message: pdfdata.Message });
            return;
        }
        // API error, e.g. a backend exception
        if (pdfdata.Status === false && pdfdata.Message) {
            dispatch(getpdfReportdataSuccess({ Status: false }));
            customAlert({ Type: 4, Message: pdfdata.Message });
        }
    }, [pdfdata]);

    useEffect(() => {
        setCashier(allLabelWithZero);                 // reset to "All" for the new party
        setCashierOptions([allLabelWithZero]);

        if (!commonPartyDropSelect.value || commonPartyDropSelect.value === 0) return;

        (async () => {
            setCashierLoading(true);
            try {
                const resp = await CashierName_Api({
                    jsonBody: JSON.stringify({ Party: commonPartyDropSelect.value }),
                });
                if (resp?.StatusCode === 200) {
                    setCashierOptions([allLabelWithZero, ...(resp.Data || [])]);
                } else if (resp?.Message) {
                    customAlert({ Type: 4, Message: resp.Message });
                }
            } catch (e) {
                customAlert({ Type: 4, Message: "Unable to load Cashiers" });
            } finally {
                setCashierLoading(false);
            }
        })();
    }, [commonPartyDropSelect]);

    const buildBody = () => ({
        FromDate: fromDate,
        ToDate: toDate,
        Party: commonPartyDropSelect.value,
        CashierID: Number(cashier?.value) || 0,
    });

    function validateFilters() {
        if (commonPartyDropSelect.value === 0) {
            customAlert({ Type: 3, Message: alertMessages.commonPartySelectionIsRequired });
            return false;
        }
        if (!fromDate || !toDate) {
            customAlert({ Type: 3, Message: "Please select From Date and To Date" });
            return false;
        }
        if (fromDate > toDate) {
            customAlert({ Type: 3, Message: "From Date cannot be after To Date" });
            return false;
        }
        return true;
    }

    function handlePrintClick() {
        if (!validateFilters()) return;
        setClickedBtn("print");

        const config = {
            ReportType: report.CashBookReport,   // see step 6
            jsonBody: JSON.stringify(buildBody()),
            PartyName: commonPartyDropSelect.label,
            CashierName: cashier?.label || "All",
            FromDate: fromDate,
            ToDate: toDate,
        };
        dispatch(getpdfReportdata(CashBookReportAPI, config));
    }

    async function handleExcelClick() {
        if (!validateFilters()) return;
        setClickedBtn("excel");
        setLoading(true);
        try {
            const response = await CashBookReportAPI({ jsonBody: JSON.stringify(buildBody()) });
            if (response?.Status === true && response.Data?.CashBookDetails?.length) {
                ExcelReportComponent({
                    excelTableData: buildExcelRows(response.Data),
                    excelFileName: "CashBook Report",
                    ExtraHeader: [
                        `Party: ${commonPartyDropSelect.label}`,
                        `Cashier: ${cashier?.label || "All"}`,
                        `From Date: ${_cfunc.date_dmy_func(fromDate)}     To Date: ${_cfunc.date_dmy_func(toDate)}`,
                    ],
                });
            } else {
                customAlert({ Type: 4, Message: response?.Message || "No records available" });
            }
        } catch (e) {
            customAlert({ Type: 4, Message: "Excel download failed" });
        } finally {
            setLoading(false);
        }
    }


    return (
        <React.Fragment>
            <MetaTags>{_cfunc.metaTagLabel(userPageAccessState)}</MetaTags>
            <div className="page-content">
                {/* ---------------- filter bar ---------------- */}
                <div className="px-2 c_card_filter text-black">
                    <Row>
                        <Col sm={3}>
                            <FormGroup className="row mt-2">
                                <Label className="col-sm-4 p-2" style={{ width: "83px" }}>FromDate</Label>
                                <Col sm="7">
                                    <C_DatePicker name="FromDate" value={fromDate} onChange={(e, date) => setFromDate(date)} />
                                </Col>
                            </FormGroup>
                        </Col>

                        <Col sm={3}>
                            <FormGroup className="row mt-2">
                                <Label className="col-sm-4 p-2" style={{ width: "65px" }}>ToDate</Label>
                                <Col sm="7">
                                    <C_DatePicker name="ToDate" value={toDate} onChange={(e, date) => setToDate(date)} />
                                </Col>
                            </FormGroup>
                        </Col>

                        <Col sm={3}>
                            <FormGroup className="row mt-2">
                                <Label className="col-sm-4 p-2" style={{ width: "70px" }}>Cashier</Label>
                                <Col sm="8">
                                    <C_Select
                                        name="Cashier"
                                        value={cashier}
                                        isSearchable={true}
                                        isClearable={true}
                                        isLoading={cashierLoading}
                                        className="react-dropdown"
                                        classNamePrefix="dropdown"
                                        styles={{ menu: (provided) => ({ ...provided, zIndex: 2 }) }}
                                        options={cashierOptions}
                                        onChange={(selected) => setCashier(selected || allLabelWithZero)}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>

                        <Col sm={3} className="d-flex justify-content-end">
                            <C_Button
                                type="button"
                                spinnerColor="white"
                                className="btn btn-primary m-3 mr"
                                onClick={handlePrintClick}
                                disabled={loading || goBtnLoading}
                                loading={clickedBtn === "print" && goBtnLoading}
                            >
                                Print
                            </C_Button>

                            <C_Button
                                type="button"
                                spinnerColor="white"
                                className="btn btn-success m-3 mr"
                                onClick={handleExcelClick}
                                disabled={loading || goBtnLoading}
                                loading={clickedBtn === "excel" && loading}
                            >
                                Excel
                            </C_Button>
                        </Col>
                    </Row>
                </div>
            </div>
            <C_Report />
        </React.Fragment>
    );
};

export default CashBookEntryReport;